import { useState } from "react";
import { Leyenda, Tooltip } from "./Figura";
import { useAncho } from "./useAncho";

export interface SerieApilada { clave: string; etiqueta: string; color: string }
export interface FilaApilada { etiqueta: string; segmentos: { clave: string; valor: number }[]; nota?: string }

/** Barras apiladas horizontales. `pct`: cada fila suma 100 %. `abs`: escala común entre filas. 2 px de superficie entre segmentos. */
export function BarraApilada({ filas, series, modo, formato, etiquetasDirectas = true, alto = 20, leyenda = true }: {
  filas: FilaApilada[]; series: SerieApilada[]; modo: "pct" | "abs"; formato: (v: number) => string; etiquetasDirectas?: boolean; alto?: number; leyenda?: boolean;
}) {
  const { ref, ancho } = useAncho();
  const [tip, setTip] = useState<{ x: number; y: number; texto: string } | null>(null);
  const totalFila = (f: FilaApilada) => f.segmentos.reduce((a, s) => a + Math.max(0, s.valor), 0);
  const maxAbs = Math.max(1e-9, ...filas.map(totalFila));
  const conEtiqueta = filas.length > 1;
  return (
    <div ref={ref} className="relative">
      <div className="flex flex-col gap-3">
        {filas.map((f) => {
          const tot = totalFila(f);
          const anchoFila = modo === "pct" ? 1 : tot / maxAbs;
          return (
            <div key={f.etiqueta}>
              {conEtiqueta && <div className="mb-1 flex justify-between text-xs"><span className="text-tinta-suave">{f.etiqueta}</span><span className="tabular text-tinta">{formato(tot)}{f.nota && <span className="text-tinta-suave"> · {f.nota}</span>}</span></div>}
              <div className="flex overflow-hidden rounded" style={{ height: alto, width: `${Math.max(anchoFila, tot > 0 ? 0.01 : 0) * 100}%`, gap: 2 }}>
                {series.map((s) => {
                  const seg = f.segmentos.find((x) => x.clave === s.clave);
                  const v = Math.max(0, seg?.valor ?? 0);
                  if (v <= 0 || tot <= 0) return null;
                  const frac = v / tot;
                  const anchoPx = frac * anchoFila * ancho;
                  const texto = `${s.etiqueta}: ${formato(v)}${modo === "pct" || filas.length === 1 ? ` (${Math.round(frac * 100)}%)` : ""}`;
                  return (
                    <div key={s.clave} tabIndex={0} aria-label={texto} className="relative flex h-full items-center justify-center overflow-visible focus:outline-2"
                      style={{ flexGrow: v, flexBasis: 0, background: s.color, minWidth: 3 }}
                      onMouseEnter={(e) => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); const c = ref.current!.getBoundingClientRect(); setTip({ x: r.left - c.left + r.width / 2, y: r.top - c.top, texto }); }}
                      onFocus={(e) => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); const c = ref.current!.getBoundingClientRect(); setTip({ x: r.left - c.left + r.width / 2, y: r.top - c.top, texto }); }}
                      onMouseLeave={() => setTip(null)} onBlur={() => setTip(null)}>
                      {etiquetasDirectas && anchoPx > 44 && frac >= 0.1 && <span className="text-[11px] font-semibold text-white">{Math.round(frac * 100)}%</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {leyenda && <Leyenda className="mt-3" items={series.filter((s) => filas.some((f) => (f.segmentos.find((x) => x.clave === s.clave)?.valor ?? 0) > 0)).map((s) => ({ etiqueta: s.etiqueta, color: s.color }))} />}
      <Tooltip visible={!!tip} x={tip?.x ?? 0} y={tip?.y ?? 0}>{tip?.texto}</Tooltip>
    </div>
  );
}
